import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

/**
 * Global listener that catches Supabase auth callbacks regardless of which URL
 * the email link lands on (homepage, etc.).
 *
 * STRICT routing:
 *   - type === "recovery"  → /redefinir-password (and ONLY this case)
 *   - type === "signup" | "magiclink" | "email_change" | "invite"
 *       → /auth/callback (which establishes the session and redirects to /meus-casos)
 *   - access_token in hash WITHOUT type → /auth/callback (safe default for signup)
 *
 * NEVER routes signup / magic link confirmations to /redefinir-password.
 */
export default function AuthRecoveryListener() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    // If we are already on a dedicated callback / reset page, do nothing.
    if (
      location.pathname === "/redefinir-password" ||
      location.pathname === "/auth/callback"
    ) {
      return;
    }

    const search = new URLSearchParams(window.location.search);
    const hashRaw = window.location.hash || "";
    const hashParams = new URLSearchParams(hashRaw.startsWith("#") ? hashRaw.slice(1) : hashRaw);

    const searchType = search.get("type");
    const hashType = hashParams.get("type");
    const type = searchType || hashType;

    // PKCE flow — Supabase appends ?code=... (query string)
    const code = search.get("code");
    if (code) {
      if (type === "recovery") {
        navigate("/redefinir-password" + window.location.search + window.location.hash, { replace: true });
      } else {
        // signup / magiclink / invite / email_change → unified callback handler
        navigate("/auth/callback" + window.location.search + window.location.hash, { replace: true });
      }
      return;
    }

    // Implicit flow — Supabase appends #access_token=...&type=<...>
    const accessToken = hashParams.get("access_token");
    if (type === "recovery") {
      navigate("/redefinir-password" + window.location.hash, { replace: true });
      return;
    }
    if (accessToken) {
      // type === "signup" | "magiclink" | "email_change" | "invite" | undefined
      navigate("/auth/callback" + window.location.hash, { replace: true });
      return;
    }

    // In-flight PASSWORD_RECOVERY event (rare — when supabase-js detects it later)
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" && window.location.pathname !== "/redefinir-password") {
        navigate("/redefinir-password", { replace: true });
      }
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, [navigate, location.pathname]);

  return null;
}
